import { createServerFn } from "@tanstack/react-start";
import { getAuthContext, enrichFriendRequest } from "@/lib/services";
import {
  listFriends,
  listRequests,
  sendRequest as dbSendRequest,
  acceptRequest as dbAcceptRequest,
  rejectRequest as dbRejectRequest,
  cancelRequest as dbCancelRequest,
  removeFriend as dbRemoveFriend,
} from "@/db/friend";
import { findByEmail } from "@/db/user";
import type { FriendView, FriendRequestView } from "@/lib/types";

export const loadFriends = createServerFn({ method: "GET" }).handler(async () => {
  const { db, userId } = await getAuthContext();

  try {
    const [friendRows, requestData] = await Promise.all([
      listFriends(db, userId),
      listRequests(db, userId),
    ]);

    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));

    const [incoming, outgoing] = await Promise.all([
      Promise.all(requestData.incoming.map((r) => enrichFriendRequest(db, r))),
      Promise.all(requestData.outgoing.map((r) => enrichFriendRequest(db, r))),
    ]);

    return { friends, incoming, outgoing };
  } catch (error) {
    console.error("Failed to load friends:", error);
    return {
      friends: [] as FriendView[],
      incoming: [] as FriendRequestView[],
      outgoing: [] as FriendRequestView[],
    };
  }
});

export const sendFriendRequestFn = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    const targetUser = await findByEmail(db, data.email);
    if (!targetUser) {
      throw new Error("No user found with that email");
    }
    if (targetUser.user_id === userId) {
      throw new Error("Cannot send friend request to yourself");
    }
    await dbSendRequest(db, userId, targetUser.user_id);
  });

export const acceptRequestFn = createServerFn({ method: "POST" })
  .inputValidator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    await dbAcceptRequest(db, Number(data.requestId), userId);
  });

export const rejectRequestFn = createServerFn({ method: "POST" })
  .inputValidator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    await dbRejectRequest(db, Number(data.requestId), userId);
  });

export const cancelRequestFn = createServerFn({ method: "POST" })
  .inputValidator((data: { requestId: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    await dbCancelRequest(db, Number(data.requestId), userId);
  });

export const removeFriendFn = createServerFn({ method: "POST" })
  .inputValidator((data: { userId: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();
    await dbRemoveFriend(db, userId, data.userId);
  });
